import React, { useState } from "react";
import {
  AppBar,
  Box,
  Container,
  Toolbar,
  Typography,
  Button,
  IconButton,
  useScrollTrigger,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";

// Import new icons
import HomeIcon from "@mui/icons-material/Home";
import ListItemIcon from "@mui/material/ListItemIcon";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SellIcon from "@mui/icons-material/Sell";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CategoryIcon from "@mui/icons-material/Category";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import LoginIcon from "@mui/icons-material/Login";
import ArticleIcon from "@mui/icons-material/Article";

import DashboardIcon from "@mui/icons-material/Dashboard";
import PersonIcon from "@mui/icons-material/Person";
import GavelIcon from "@mui/icons-material/Gavel";
import ReceiptIcon from "@mui/icons-material/Receipt";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AccountCircle from "@mui/icons-material/AccountCircle";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logoutUser, setCredentials } from "../../features/auth/authSlice";
import Logo from "../../assets/logo.svg";
import VerifyOtpModal from "./VerifyOtpModal";
import { switchRole, fetchUserAddresses } from "../../features/user/userSlice";
import axios from "axios";
import { setAuthToken } from "../../services/apiClient";
import Loader from "../common/Loader";

// ... (tabs and myAccountTabs arrays remain the same) ...
const tabs = [
  { label: "Home", path: "/home" },
  { label: "Market", path: "/market" },
  { label: "Buy Scrap", path: "/buy" },
  { label: "Sell Scrap", path: "/sell" },
  { label: "How It Works", path: "/how-it-works" },
  { label: "Products", path: "/products" },
  { label: "Pricing", path: "/pricing" },
  { label: "News & Blogs", path: "/blog" },
  // { label: "New Market", path: "/new-market" },
  //{ label: "Market", path: "/market" },
  // { label: "Contact Us", path: "/contact-us" },
];

const myAccountTabs = [
  { label: "Dashboard", path: "/dashboard" },
  { label: "Profile", path: "/profile" },
  { label: "Profile Verification", path: "/kyc-verification" },
  { label: "My Bids", path: "/my-bids" },
  { label: "My Orders", path: "/my-orders" },
  { label: "My Requested Bids", path: "/my-requested-bids" },
  { label: "My Offers", path: "/my-offers" },
  { label: "My Transactions", path: "/my-transactions" },
  { label: "Range Requests", path: "/range-requests" },
  { label: "Notifications", path: "/notifications" },
  // { label: "My Chats", path: "/chat" },
  // { label: "Profile Overview", path: "/profile-overview" },
];

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { token, role, roles, user } = useSelector((state) => state.auth);
  // console.log("Roles:", roles);
  const isLoggedIn = Boolean(token); // New variable
  const isBuyerLoggedIn = Boolean(token && role === "buyer");
  const isSellerLoggedIn = Boolean(token && role === "seller");

  const notifications = useSelector((state) => state.user.notifications || []);
  const unreadCount = notifications.filter((n) => n.is_read === 0).length;

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isScrolled = useScrollTrigger({ threshold: 10 });
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [pendingRole, setPendingRole] = useState(null);
  const [isSwitching, setIsSwitching] = useState(false);

  const isPathActive = (path) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(path);

  const handleDropdownToggle = (event) => {
    setAnchorEl(event.currentTarget);
    setDropdownOpen((prev) => !prev);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
    setDropdownOpen(false);
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    handleDropdownClose();
    setMobileMenuOpen(false);
  };

  // const handleRoleSwitch = async (targetRole) => {
  //   if (!roles?.includes(targetRole)) {
  //     navigate(targetRole === "seller" ? "/sell" : "/buy");
  //     return;
  //   }
  //   try {
  //     const result = await dispatch(switchRole({ role: targetRole })).unwrap();
  //     // console.log("Switch Role Response:", result);

  //     if (result?.status) {
  //       // ✅ OTP sent successfully
  //       setPendingRole(targetRole);
  //       setShowOtpModal(true);
  //     } else {
  //       console.error("Failed to send OTP:", result?.message);
  //     }
  //   } catch (error) {
  //     console.error("Role switch failed:", error);
  //   }
  // };

  const handleRoleSwitch = async (targetRole) => {
    if (!roles?.includes(targetRole)) {
      navigate(targetRole === "seller" ? "/sell" : "/buy");
      return;
    }

    setIsSwitching(true);
    try {
      // 🚀 State synchronization (token, role, persistence) is now handled 
      // automatically by authSlice.extraReducers for switchRole.fulfilled
      const result = await dispatch(switchRole({ role: targetRole })).unwrap();

      // EXTRA: If switching to seller, fetch address count to ensure proper redirection
      if (targetRole === "seller") {
        try {
          const addressRes = await dispatch(fetchUserAddresses()).unwrap();
          const addressList = addressRes?.data || addressRes;
          const addressCount = Array.isArray(addressList) ? addressList.length : 0;

          // Re-sync: merge freshUser ON TOP of existing user to preserve verification flags
          const freshUser = result?.data?.user || result?.user;
          const updatedRoles = roles?.includes(targetRole)
            ? roles
            : [...(roles || []), targetRole];
          const mergedUser = { ...user, ...(freshUser || {}), addresses_count: addressCount, roles: updatedRoles };
          if (mergedUser) {
            dispatch(
              setCredentials({
                user: mergedUser,
                role: targetRole,
                // token: result?.data?.token || result?.token || result?.data?.access_token || result?.access_token,
              })
            );
          }
        } catch (addrErr) {
          console.error("Error fetching addresses during switch:", addrErr);
        }
      }

      // After updating state and headers, navigate
      if (targetRole === "seller") {
        navigate("/seller/dashboard");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Role switch failed:", error);
      setIsSwitching(false);
    }
  };

  const handleBuyScrapClick = () => {
    // If user not logged in → go to login
    if (!isLoggedIn) {
      navigate("/buy");
      return;
    }

    // If logged in as seller but doesn’t have buyer role → go to buyer landing
    if (role === "seller" && !roles?.includes("buyer")) {
      navigate("/buy");
      return;
    }

    // If already has buyer role → switch properly
    if (role === "seller" && roles?.includes("buyer")) {
      handleRoleSwitch("buyer");
      return;
    }

    // Default fallback
    navigate("/buy");
  };

  const handleSellScrapClick = () => {
    // If user not logged in → go to login
    if (!isLoggedIn) {
      navigate("/sell");
      return;
    }

    // If logged in as buyer but doesn’t have seller role → go to seller landing
    if (role === "buyer" && !roles?.includes("seller")) {
      navigate("/sell");
      return;
    }

    // If already a seller → just switch to seller flow
    if (role === "buyer" && roles?.includes("seller")) {
      handleRoleSwitch("seller");
      return;
    }

    // If currently a seller -> Go to seller dashboard
    if (role === 'seller') {
      navigate('/seller/dashboard');
      return;
    }

    // Default fallback
    navigate("/sell");
  };

  const handleOtpSuccess = () => {
    setShowOtpModal(false);
    setPendingRole(null);
  };

  const handleMobileMenuOpen = () => setMobileMenuOpen(true);
  const handleMobileMenuClose = () => setMobileMenuOpen(false);

  // ...existing code...
  const filteredTabs = (() => {
    const excludeForGuests = ["News & Blogs", "Products"];
    const excludeForBuyer = ["News & Blogs"];
    const excludeForSeller = ["News & Blogs"];

    let exclude = [];
    if (!isLoggedIn) exclude = excludeForGuests;
    else if (isSellerLoggedIn) exclude = excludeForSeller;
    else if (isBuyerLoggedIn) exclude = excludeForBuyer;

    return tabs.filter((tab) => !exclude.includes(tab.label));
  })();

  const iconMap = {
    Home: <HomeIcon />,
    Market: <StorefrontIcon />,
    "Buy Scrap": <ShoppingCartIcon />,
    "Sell Scrap": <SellIcon />,
    "How It Works": <HelpOutlineIcon />,
    Products: <CategoryIcon />,
    // "New Market": <StorefrontIcon />,
    "News & Blogs": <ArticleIcon />,
    Dashboard: <DashboardIcon />,
    Profile: <PersonIcon />,
    "My Bids": <GavelIcon />,
    "My Orders": <ReceiptIcon />,
    Notifications: <NotificationsIcon />,
    "My Requested Bids": <GavelIcon />,
    Logout: <PersonIcon />,
    Login: <LoginIcon />,
    "Get Started": <VpnKeyIcon />,
  };

  return (
    <>
      {isSwitching && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Loader />
          {/* <Typography
            variant="h6"
            sx={{ mt: 2, color: "primary.main", fontWeight: 600 }}
          >
            // Switching Dashboard...
          </Typography> */}
        </Box>
      )}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: isScrolled
            ? "linear-gradient(135deg, rgba(244, 197, 66, 0.98), rgba(230, 183, 58, 0.98))"
            : "linear-gradient(135deg, #F4C542, #E6B73A)",
          backdropFilter: isScrolled ? "blur(20px)" : "none",
          py: { xs: 0.5, md: 1 },
          boxShadow: isScrolled
            ? "0 10px 40px rgba(244, 197, 66, 0.3)"
            : "0 5px 20px rgba(244, 197, 66, 0.2)",
          zIndex: 1300,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              px: { xs: 2, sm: 3, md: 4 }, // Padding adjustments for better spacing
              minHeight: { xs: 48, sm: 56, md: 60 },
            }}
          >
            {/* Logo Section */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                cursor: "pointer",
              }}
              onClick={() => navigate("/home")}
            >
              <Box
                component="img"
                src={Logo}
                alt="ScrapTrade Logo"
                sx={{
                  width: { xs: "150px", sm: "180px", md: "200px" }, // Adjust logo size
                  height: "auto", // Maintain aspect ratio
                }}
              />
            </Box>

            {/* Navigation Tabs - shown only on md and up */}
            <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1 }}>
              {filteredTabs.map((tab) => {
                const isBuy = tab.label === "Buy Scrap";
                const isSell = tab.label === "Sell Scrap";
                const handleClick = isBuy
                  ? handleBuyScrapClick
                  : isSell
                    ? handleSellScrapClick
                    : () => navigate(tab.path);

                return (
                  <Button
                    key={tab.label}
                    onClick={handleClick}
                    sx={{
                      color: isPathActive(tab.path) ? "#1a2118" : "#2D3A27",
                      fontWeight: 600,
                      px: { xs: 1, sm: 1.5, md: 1 }, // Adjust padding for better fit
                      py: { xs: 0.75, sm: 1 },
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: { xs: "12px", sm: "13px", md: "14px" },
                      backgroundColor: isPathActive(tab.path)
                        ? "rgba(45, 58, 39, 0.1)"
                        : "transparent",
                      "&:hover": {
                        background: "rgba(45, 58, 39, 0.1)",
                        transform: "translateY(-1px)",
                      },
                      whiteSpace: "nowrap",
                    }}
                  >
                    {tab.label}
                  </Button>
                );
              })}

              {isLoggedIn && (
                <Box>
                  <Button
                    onClick={handleDropdownToggle}
                    startIcon={<AccountCircle />}
                    sx={{
                      color: "#2D3A27",
                      fontWeight: 600,
                      px: 2,
                      py: 1.5,
                      borderRadius: "12px",
                      textTransform: "none",
                      fontSize: "16px",
                    }}
                  >
                    My Account
                  </Button>
                  <Menu
                    anchorEl={anchorEl}
                    open={dropdownOpen}
                    onClose={handleDropdownClose}
                    sx={{
                      mt: 1,
                      "& .MuiPaper-root": {
                        borderRadius: "12px",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                      },
                    }}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                  >
                    {myAccountTabs.map((tab) => (
                      <MenuItem
                        key={tab.label}
                        onClick={() => {
                          navigate(tab.path);
                          handleDropdownClose();
                        }}
                        sx={{
                          py: 0.5,
                          px: 2,
                          minHeight: "auto",
                          "&:hover": {
                            backgroundColor: "rgba(45, 58, 39, 0.05)",
                          },
                        }}
                      >
                        {tab.label}
                        {tab.label === "Notifications" && unreadCount > 0 && (
                          <Box
                            component="span"
                            sx={{
                              ml: 2,
                              background: "#d32f2f",
                              color: "#fff",
                              borderRadius: "50%",
                              px: 1,
                              fontSize: "12px",
                              fontWeight: "bold",
                              minWidth: 24,
                              textAlign: "center",
                            }}
                          >
                            {unreadCount}
                          </Box>
                        )}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              )}
            </Box>

            {/* Right Actions */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {!isLoggedIn && (
                <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1.5 }}>
                  <Button
                    onClick={() => navigate("/login", { state: { role: "buyer" } })}
                    variant="outlined"
                    sx={{
                      borderColor: "#2D3A27",
                      color: "#2D3A27",
                      borderRadius: "12px",
                      px: 3,
                      py: 1,
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: { xs: "12px", sm: "13px", md: "14px" },
                      whiteSpace: "nowrap",
                    }}
                  >
                    Login
                  </Button>
                  <Button
                    onClick={() => navigate("/register")}
                    variant="contained"
                    sx={{
                      background: "linear-gradient(135deg, #2D3A27, #1a2118)",
                      color: "#fff",
                      borderRadius: "12px",
                      px: 4,
                      py: 1,
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: { xs: "12px", sm: "13px", md: "14px" },
                      boxShadow: "0 8px 32px rgba(45, 58, 39, 0.4)",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: "0 12px 40px rgba(45, 58, 39, 0.6)",
                      },
                      whiteSpace: "nowrap",
                    }}
                  >
                    Get Started
                  </Button>
                  {/* <Button
                    onClick={() => navigate("/login", { state: { role: "seller" } })}
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      borderRadius: "24px", // even smoother curve
                      px: 4,
                      py: 1,
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: { xs: "12px", sm: "13px", md: "14px" },
                      whiteSpace: "nowrap",
                      color: "#2D3A27",

                      // Glass background (always premium)
                      background:
                        "linear-gradient(135deg, rgba(255,255,255,0.55), rgba(255,255,255,0.12)), linear-gradient(135deg, rgba(45,58,39,0.18), rgba(45,58,39,0.03))",

                      // Soft glass border (not black)
                      border: "1px solid rgba(45, 58, 39, 0.18)",

                      backdropFilter: "blur(14px)",
                      WebkitBackdropFilter: "blur(14px)",

                      // Premium depth (always visible)
                      boxShadow:
                        "0 10px 28px rgba(45, 58, 39, 0.22), inset 0 1px 2px rgba(255,255,255,0.7)",

                      transition: "all 0.35s ease",

                      // Glossy shine (always visible)
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: "-30%",
                        width: "55%",
                        height: "100%",
                        background:
                          "linear-gradient(120deg, rgba(255,255,255,0.5), rgba(255,255,255,0))",
                        transform: "skewX(-20deg)",
                        opacity: 0.7,
                        transition: "all 0.6s ease",
                      },

                      "&:hover::before": {
                        left: "120%",
                        opacity: 1,
                      },

                      "&:hover": {
                        transform: "translateY(-1.5px)",

                        // softer glow instead of black border
                        boxShadow:
                          "0 14px 36px rgba(45, 58, 39, 0.28), inset 0 1px 2px rgba(255,255,255,0.85)",

                        border: "1px solid rgba(45, 58, 39, 0.28)",

                        background:
                          "linear-gradient(135deg, rgba(255,255,255,0.65), rgba(255,255,255,0.18)), linear-gradient(135deg, rgba(45,58,39,0.22), rgba(45,58,39,0.05))",
                      },
                    }}
                  >
                    Start Selling
                  </Button> */}
                </Box>
              )}

              {isLoggedIn && (
                <Button
                  variant="outlined"
                  onClick={handleLogout}
                  sx={{
                    borderColor: "#2D3A27",
                    color: "#2D3A27",
                    borderRadius: "12px",
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: "16px",
                    display: { xs: "none", md: "inline-flex" },
                  }}
                >
                  Logout
                </Button>
              )}

              {isMobile && (
                <IconButton
                  onClick={handleMobileMenuOpen}
                  sx={{
                    color: "#2D3A27",
                    background: "rgba(45, 58, 39, 0.1)",
                    "&:hover": { background: "rgba(45, 58, 39, 0.2)" },
                    ml: 2,
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Box sx={{ mt: { xs: 6, md: 8 } }} />

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: 280,
            background: "rgba(255, 255, 255, 0.85)",
            backdropFilter: "blur(12px)",
            // borderTopRightRadius: 24,
            // borderBottomRightRadius: 24,
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
            px: 1,
            py: 2,
          },
        }}
      >
        <Toolbar />
        <Box
          role="presentation"
          onClick={handleMobileMenuClose}
          onKeyDown={handleMobileMenuClose}
          sx={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
          <Box
            sx={{
              px: 3,
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
              cursor: "pointer",
            }}
            onClick={() => navigate("/")}
          ></Box>

          <Divider sx={{ mb: 1 }} />

          <List sx={{ flexGrow: 1 }}>
            {filteredTabs.map((tab) => (
              <ListItem key={tab.label} disablePadding>
                <ListItemButton
                  onClick={() => navigate(tab.path)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    "&:hover": {
                      backgroundColor: "rgba(45, 58, 39, 0.15)",
                      transform: "scale(1.05)",
                      transition: "all 0.25s ease",
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: "#2D3A27" }}>
                    {iconMap[tab.label]}
                  </ListItemIcon>
                  <ListItemText
                    primary={tab.label}
                    primaryTypographyProps={{
                      fontWeight: isPathActive(tab.path) ? "bold" : "regular",
                      color: isPathActive(tab.path) ? "#1a2118" : "#2D3A27",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          {isBuyerLoggedIn || isSellerLoggedIn && (
            <>
              <Divider sx={{ mt: 1, mb: 1 }} />
              <Typography
                sx={{ px: 3, mb: 1, fontWeight: 600, color: "#2D3A27" }}
              >
                My Account
              </Typography>
              <List>
                {myAccountTabs.map((tab) => (
                  <ListItem key={tab.label} disablePadding>
                    <ListItemButton
                      onClick={() => navigate(tab.path)}
                      sx={{
                        borderRadius: 2,
                        mb: 0.5,
                        "&:hover": {
                          backgroundColor: "rgba(45, 58, 39, 0.15)",
                          transform: "scale(1.05)",
                          transition: "all 0.25s ease",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ color: "#2D3A27" }}>
                        {iconMap[tab.label]}
                      </ListItemIcon>
                      <ListItemText
                        primary={tab.label}
                        primaryTypographyProps={{
                          fontWeight: "regular",
                          color: "#2D3A27",
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={handleLogout}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      color: "#d32f2f",
                      "&:hover": {
                        backgroundColor: "rgba(211, 47, 47, 0.15)",
                        transform: "scale(1.05)",
                        transition: "all 0.25s ease",
                        color: "#b71c1c",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: "#d32f2f" }}>
                      {iconMap["Logout"]}
                    </ListItemIcon>
                    <ListItemText primary="Logout" />
                  </ListItemButton>
                </ListItem>
              </List>
            </>
          )}

          {/* This is the section for logged out users */}
          {!isLoggedIn && (
            <>
              <Divider sx={{ mt: 1, mb: 1 }} />
              <List>
                {/* Login Button with Icon */}
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => navigate("/login")}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      "&:hover": {
                        backgroundColor: "rgba(45, 58, 39, 0.15)",
                        transform: "scale(1.05)",
                        transition: "all 0.25s ease",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: "#2D3A27" }}>
                      {iconMap["Login"]} {/* Use the mapped icon */}
                    </ListItemIcon>
                    <ListItemText primary="Login" />
                  </ListItemButton>
                </ListItem>

                {/* Get Started Button with Icon */}
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => navigate("/register")}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      "&:hover": {
                        backgroundColor: "rgba(45, 58, 39, 0.15)",
                        transform: "scale(1.05)",
                        transition: "all 0.25s ease",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: "#2D3A27" }}>
                      {iconMap["Get Started"]} {/* Use the mapped icon */}
                    </ListItemIcon>
                    <ListItemText primary="Get Started" />
                  </ListItemButton>
                </ListItem>
              </List>
            </>
          )}
        </Box>
      </Drawer>

      <VerifyOtpModal
        show={showOtpModal}
        onClose={() => setShowOtpModal(false)}
        onSuccess={handleOtpSuccess}
        role={pendingRole}
      />
    </>
  );
};

export default Header;

{
  /* <Box
                  sx={{
                    width: 44,
                    height: 44,
                    background: "linear-gradient(135deg, #2D3A27, #1a2118)",
                    borderRadius: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 8px 32px rgba(45, 58, 39, 0.3)",
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: -2,
                      background: "linear-gradient(135deg, #F4C542, #E6B73A)",
                      borderRadius: "18px",
                      zIndex: -1,
                    },
                  }}
                >
                  <Typography
                    sx={{ color: "#fff", fontWeight: 800, fontSize: "24px" }}
                  >
                    S
                  </Typography>
                </Box> */
}
{
  /* Typography with partially white text */
}
{
  /* <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    fontSize: "28px",
                    letterSpacing: "-0.5px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box component="span" sx={{ color: "#2D3A27" }}>
                    Scrap
                  </Box>
                  <Box component="span" sx={{ color: "#2D3A27" }}>
                    Trade
                  </Box>
                </Typography> */
}

{
  /* <Box
                sx={{
                  width: 48,
                  height: 48,
                  background: "linear-gradient(135deg, #2D3A27, #1a2118)",
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 8px 32px rgba(45, 58, 39, 0.3)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 24,
                }}
              >
                S
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#2D3A27" }}>
                ScrapTrade
              </Typography> */
}
